/**
 * Composable for ranking accounts by usage frequency with time decay.
 * Stores usage history in localStorage and calculates relevance scores.
 */

const STORAGE_KEY = 'a55-admin-account-usage'
const DECAY_LAMBDA = 0.1 // Higher = faster decay. 0.1 means ~50% decay after 7 days

interface UsageRecord {
  accountId: string
  timestamp: number
}

interface StoredUsage {
  records: UsageRecord[]
}

function loadUsageHistory(): UsageRecord[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as StoredUsage
      return parsed.records || []
    }
  } catch (e) {
    console.warn('Failed to load account usage history:', e)
  }
  return []
}

function saveUsageHistory(records: UsageRecord[]): void {
  // Keep only last 100 records to prevent unbounded growth
  const trimmedRecords = records.slice(-100)

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ records: trimmedRecords }))
  } catch (e) {
    console.warn('Failed to save account usage history:', e)
  }
}

/**
 * Calculate score for an account based on usage history with exponential decay.
 * Score = sum of e^(-λ * daysSinceUsage) for each usage record
 */
function calculateScore(accountId: string, records: UsageRecord[]): number {
  const now = Date.now()
  const msPerDay = 24 * 60 * 60 * 1000

  return records
    .filter(r => r.accountId === accountId)
    .reduce((score, record) => {
      const daysSinceUsage = (now - record.timestamp) / msPerDay
      const decayedWeight = Math.exp(-DECAY_LAMBDA * daysSinceUsage)
      return score + decayedWeight
    }, 0)
}

export function useAccountRanking<T extends { entity_uuid: string }>() {
  const usageRecords = ref<UsageRecord[]>([])

  usageRecords.value = loadUsageHistory()

  /**
   * Record that an account was selected/used
   */
  function recordUsage(accountId: string): void {
    usageRecords.value.push({
      accountId,
      timestamp: Date.now()
    })
    saveUsageHistory(usageRecords.value)
  }

  /**
   * Get score for a specific account (higher = more relevant)
   */
  function getScore(accountId: string): number {
    return calculateScore(accountId, usageRecords.value)
  }

  /**
   * Sort accounts by usage score (most used first), preserving original order for unranked
   */
  function sortByRanking(accounts: T[]): T[] {
    const scored = accounts.map(account => ({
      account,
      score: getScore(account.entity_uuid)
    }))

    // Sort by score descending, but keep original order for accounts with 0 score
    return scored
      .sort((a, b) => b.score - a.score)
      .map(s => s.account)
  }

  /**
   * Check if an account has been used before
   */
  function hasUsageHistory(accountId: string): boolean {
    return usageRecords.value.some(r => r.accountId === accountId)
  }

  return {
    recordUsage,
    getScore,
    sortByRanking,
    hasUsageHistory
  }
}
